// Audio device detection module
// Handles device enumeration, selection, and categorization

class AudioDeviceDetection {
    constructor(logCallback, logWarnCallback, logErrorCallback) {
        this.log = logCallback || (() => {});
        this.logWarn = logWarnCallback || (() => {});
        this.logError = logErrorCallback || (() => {});
    }
    
    // Helper method to create a delay promise
    delay(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    
    // Helper function to safely truncate device IDs for logging
    truncateDeviceId(deviceId, maxLength = 12) {
        if (!deviceId) return 'unknown';
        return deviceId.substring(0, maxLength) + '...';
    }
    
    // Check if a device is a built-in microphone based on its label
    isBuiltInDevice(label) {
        const lowerLabel = label.toLowerCase();
        const builtInIndicators = [
            'built-in', 
            'internal', 
            'macbook', 
            'imac', 
            'default - built-in',
            'default microphone',
            'default audio device'
        ];
        return builtInIndicators.some(indicator => lowerLabel.includes(indicator));
    }
    
    // Check if a device is likely a USB or external audio device
    isUsbOrExternalDevice(label) {
        const lowerLabel = label.toLowerCase();
        const externalIndicators = [
            'usb',
            'external',
            'audio interface',
            'aggregate',
            'gp5',
            'gp-5',
            'guitar port',
            'line 6',
            'line6',
            'focusrite',
            'scarlett',
            'behringer',
            'presonus',
            'motu',
            'steinberg',
            'roland',
            'zoom',
            'apogee',
            'universal audio',
            'rme',
            'audient'
        ];
        return externalIndicators.some(indicator => lowerLabel.includes(indicator));
    }
    
    // Helper method to categorize an audio input device
    categorizeAudioDevice(device) {
        const label = device.label?.trim();
        const isUsb = label && this.isUsbOrExternalDevice(label);
        const isBuiltIn = label && this.isBuiltInDevice(label);
        return {
            isUsb,
            isBuiltIn,
            category: isUsb ? 'USB/External' : isBuiltIn ? 'Built-in' : 'Unknown'
        };
    }
    
    // Request microphone permission
    async requestDevicePermission() {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            stream.getTracks().forEach(track => track.stop());
            return true;
        } catch (error) {
            this.logError('Error requesting device permission:', error);
            return false;
        }
    }
    
    // Select preferred audio device, prioritizing USB devices over built-in microphones
    async selectPreferredAudioDevice() {
        try {
            this.log('Requesting microphone permission...');
            await this.requestDevicePermission();
            
            // Small delay for browser to update device list
            await this.delay(100);
            
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices.filter(device => device.kind === 'audioinput');
            
            if (audioInputs.length === 0) {
                this.logWarn('No audio input devices found');
                return null;
            }
            
            // Categorize all devices once and log them
            const categorizedDevices = audioInputs.map(device => ({
                device,
                info: this.categorizeAudioDevice(device)
            }));
            
            this.log('Available audio input devices:');
            categorizedDevices.forEach(({ device, info }, index) => {
                const category = info.isUsb ? '[USB/External]' : info.isBuiltIn ? '[Built-in]' : '[Unknown]';
                this.log(`  [${index}] ${category} ${device.label || '(no label)'}`);
            });
            
            // Find USB/external device
            const usbDeviceEntry = categorizedDevices.find(({ device, info }) => {
                return device.label?.trim() && info.isUsb;
            });
            
            if (usbDeviceEntry) {
                this.log('Selected USB/external device:', usbDeviceEntry.device.label);
                return usbDeviceEntry.device.deviceId;
            }
            
            // Find non-built-in device
            const nonBuiltInDeviceEntry = categorizedDevices.find(({ device, info }) => {
                return device.label?.trim() && !info.isBuiltIn;
            });
            
            if (nonBuiltInDeviceEntry) {
                this.log('Selected non-built-in device:', nonBuiltInDeviceEntry.device.label);
                return nonBuiltInDeviceEntry.device.deviceId;
            }
            
            this.log('No USB/external device found, using browser default');
            return null;
            
        } catch (error) {
            this.logError('Error selecting audio device:', error);
            return null;
        }
    }
    
    // Get list of available audio input devices
    async getAvailableDevices() {
        try {
            this.log('Enumerating devices...');
            
            let devices = await navigator.mediaDevices.enumerateDevices();
            let audioInputs = devices.filter(device => device.kind === 'audioinput');
            
            // Check if we have labels - if not, request permission
            const hasLabels = audioInputs.some(device => device.label?.trim());
            
            if (!hasLabels) {
                this.log('Requesting permission for device labels...');
                await this.requestDevicePermission();
                await this.delay(100);
                
                devices = await navigator.mediaDevices.enumerateDevices();
                audioInputs = devices.filter(device => device.kind === 'audioinput');
            }
            
            return audioInputs.map(device => {
                const label = device.label || `Audio Input ${device.deviceId.substring(0, 8)}`;
                const deviceInfo = this.categorizeAudioDevice(device);
                
                return {
                    deviceId: device.deviceId,
                    label: label,
                    groupId: device.groupId,
                    isUsb: deviceInfo.isUsb,
                    isBuiltIn: deviceInfo.isBuiltIn
                };
            });
        } catch (error) {
            this.logError('Error getting available devices:', error);
            return [];
        }
    }
}

export default AudioDeviceDetection;
